import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Bot, User, Loader2, Sparkles, Minimize2, Maximize2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const AICareerCoach = ({ user = null, roadmaps = [], subscription = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: `Hey ${user?.displayName?.split(' ')[0] || 'there'}! 👋 I'm your AI Career Coach. Ask me anything about:\n\n• Career path decisions\n• Skill recommendations\n• Interview preparation tips\n• Salary negotiation advice\n• Learning resources\n\nWhat's on your mind?`
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && !isMinimized) {
            inputRef.current?.focus();
        }
    }, [isOpen, isMinimized]);

    const quickQuestions = [
        "What skills should I focus on?",
        "How to prepare for interviews?",
        "Is my career path right for me?",
        "How to negotiate salary?"
    ];

    const getLocalFallback = (userMessage, careerPath, progress) => {
        const msgLower = userMessage.toLowerCase();

        if (msgLower.includes('skill') || msgLower.includes('learn') || msgLower.includes('focus')) {
            return `Based on your ${careerPath} journey (${progress}% complete), here are my top recommendations:\n\n**Immediate Focus:**\n• Strengthen your core technical skills through hands-on projects\n• Practice problem-solving daily (even 30 mins counts!)\n\n**This Month:**\n• Build at least one portfolio project\n• Start contributing to open-source\n\n**Next Quarter:**\n• Deep dive into system design\n• Begin mock interviews\n\n💡 *Tip: Consistency beats intensity. 1 hour daily > 7 hours on weekends.*`;
        }

        if (msgLower.includes('interview') || msgLower.includes('prepare')) {
            return `Great question! For ${careerPath} interviews:\n\n**Technical Rounds:**\n• Practice coding on LeetCode/HackerRank (focus on Medium difficulty)\n• Study data structures: arrays, trees, graphs, hash maps\n• Learn at least 2-3 system design patterns\n\n**Behavioral Rounds:**\n• Prepare 5-6 STAR method stories\n• Focus on: leadership, conflict resolution, and failure stories\n• Research the company's culture and recent news\n\n**Pro Tips:**\n• Do at least 3-5 mock interviews\n• Record yourself answering questions\n• Prepare thoughtful questions for the interviewer\n\n🎯 *Check out the Interview Prep tab for AI-powered practice!*`;
        }

        if (msgLower.includes('salary') || msgLower.includes('negotiat') || msgLower.includes('pay') || msgLower.includes('compensation')) {
            return `Let's talk compensation strategy for ${careerPath}:\n\n**Research Phase:**\n• Check Glassdoor, Levels.fyi, and Blind for salary data\n• Network with people at target companies\n• Understand the total comp structure (base + bonus + equity)\n\n**Negotiation Tips:**\n• Never share your current salary first\n• Always have multiple offers if possible\n• Focus on total compensation, not just base\n• Use data: "Based on market research, the range for this role is..."\n\n**Common Mistakes:**\n❌ Accepting the first offer without negotiating\n❌ Focusing only on base salary\n❌ Not asking about growth trajectory\n\n💰 *Check the Salary Trajectory widget for projections specific to your path!*`;
        }

        if (msgLower.includes('motivat') || msgLower.includes('stuck') || msgLower.includes('overwhelm') || msgLower.includes('lost')) {
            return `I hear you, and this is completely normal! 💪\n\n**Remember:**\n• Every expert was once a beginner\n• Progress isn't always linear - plateaus are normal\n• You're already ahead of 90% of people by taking action\n\n**When Feeling Stuck:**\n1. Break the current task into smaller steps\n2. Take a short break (seriously, it helps!)\n3. Work on something fun within your field\n4. Connect with others on a similar journey\n\n**Your Progress:**\nYou're at ${progress}% on your ${careerPath} roadmap. That's real progress! 🎯\n\nKeep going - future you will thank present you! 🚀`;
        }

        return null; // No local match
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsLoading(true);

        const activeRoadmap = roadmaps.find(r => r.isActive) || roadmaps[0];
        const careerPath = activeRoadmap?.careerPath || 'your chosen career';
        const progress = activeRoadmap?.progress?.progressPercentage || 0;

        try {
            // Try real AI backend first
            const result = await featuresAPI.coachChat(userMessage, {
                career_path: careerPath,
                progress,
            });
            setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
        } catch (err) {
            // Fallback to local pattern matching
            const fallback = getLocalFallback(userMessage, careerPath, progress)
                || `That's a great question about ${careerPath}! Based on your progress (${progress}%), keep focusing on consistent daily practice and building projects. Ask me about skills, interviews, salary, or resources for more specific help! 😊`;
            setMessages(prev => [...prev, { role: 'assistant', content: fallback }]);
        }

        setIsLoading(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Pulse indicator when closed
    const unreadCount = isOpen ? 0 : 1;

    return (
        <>
            {/* Floating Button */}
            <AnimatePresence>
                {!isOpen && (
                    <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setIsOpen(true)}
                        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-xl shadow-emerald-500/30 flex items-center justify-center text-white"
                    >
                        <MessageCircle className="w-6 h-6" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center animate-pulse">
                                {unreadCount}
                            </span>
                        )}
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className={`fixed z-50 bg-[#0B0F19] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden ${isMinimized
                            ? 'bottom-6 right-6 w-72 rounded-2xl'
                            : 'bottom-6 right-6 w-96 h-[560px] rounded-2xl flex flex-col'
                            }`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-b border-white/10">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-white flex items-center gap-1">
                                        AI Career Coach
                                        <Sparkles className="w-3 h-3 text-amber-400" />
                                    </div>
                                    <div className="text-[10px] text-emerald-400">Online • Ready to help</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setIsMinimized(!isMinimized)}
                                    className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                                >
                                    {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                                </button>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {!isMinimized && (
                            <>
                                {/* Messages */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                                    {messages.map((msg, i) => (
                                        <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            {msg.role === 'assistant' && (
                                                <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                                                    <Bot className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${msg.role === 'user'
                                                ? 'bg-emerald-500/20 text-white rounded-br-md'
                                                : 'bg-white/5 text-slate-300 rounded-bl-md'
                                                }`}>
                                                {msg.content.split('\n').map((line, j) => (
                                                    <span key={j}>
                                                        {line.startsWith('**') && line.endsWith('**')
                                                            ? <strong className="text-white">{line.replace(/\*\*/g, '')}</strong>
                                                            : line.startsWith('• ')
                                                                ? <span className="block ml-2">{line}</span>
                                                                : line.startsWith('✅') || line.startsWith('❌') || line.startsWith('💡') || line.startsWith('🎯') || line.startsWith('💰') || line.startsWith('📚') || line.startsWith('🔄') || line.startsWith('🚀') || line.startsWith('💪')
                                                                    ? <span className="block">{line}</span>
                                                                    : line
                                                        }
                                                        {j < msg.content.split('\n').length - 1 && <br />}
                                                    </span>
                                                ))}
                                            </div>
                                            {msg.role === 'user' && (
                                                <div className="w-6 h-6 bg-slate-700 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                                                    <User className="w-3 h-3 text-slate-300" />
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {isLoading && (
                                        <div className="flex gap-2 items-start">
                                            <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center shrink-0">
                                                <Bot className="w-3 h-3 text-white" />
                                            </div>
                                            <div className="bg-white/5 rounded-2xl rounded-bl-md px-4 py-3">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Quick Questions */}
                                {messages.length <= 1 && (
                                    <div className="px-4 pb-2">
                                        <div className="flex flex-wrap gap-1.5">
                                            {quickQuestions.map((q, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => { setInput(q); }}
                                                    className="text-[11px] px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:border-emerald-500/30 transition-colors"
                                                >
                                                    {q}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Input */}
                                <div className="border-t border-white/10 p-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            placeholder="Ask your career question..."
                                            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
                                        />
                                        <button
                                            onClick={handleSend}
                                            disabled={!input.trim() || isLoading}
                                            className="w-10 h-10 bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 disabled:text-slate-500 rounded-xl flex items-center justify-center text-white transition-colors"
                                        >
                                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AICareerCoach;
